import { Table, TableColumnType, TableProps } from 'antd'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Response } from '../../api/util/with-response-formatter-interceptor'
import { Task } from '../../models/models'
import { toastFailure } from '../../util/toast'
import useDebounce from '../project-master/single-project-view/hooks'

interface Props {
    pagination?: boolean
    //antd ColumnType<T> does not have title property
    columns?: Array<TableColumnType<Task>>
    actionComponents?: Array<(taskId: number) => JSX.Element>
    dependencies?: any
    request: (pagination?: {
        skip: number
        limit: number
    }) => Promise<Response<{ count: number; entities: Task[] }>>
}

export const TasksTableColumns: { [key in string]: TableColumnType<Task> } = {
    Id: {
        title: 'Id',
        key: 'id',
        render: (task: Task) => {
            return task.id
        },
    },
    Project: {
        title: 'Project',
        key: 'project',
        render: (task: Task) => {
            return task.project?.name
        },
    },
    Type: {
        title: 'Type',
        key: 'type',
        render: (task: Task) => {
            return task?.type?.title
        },
    },
    Description: {
        title: 'Description',
        key: 'description',
        width: '350px',
        render: (task: Task) => {
            return task.description
        },
    },
    Author: {
        title: 'Created by',
        key: 'createdBy',
        render: (task: Task) => {
            return task?.createdByUser
                ? `${task?.createdByUser?.firstName} ${task?.createdByUser?.lastName}`
                : 'None'
        },
    },
    CompletedAt: {
        title: 'Completed at',
        key: 'completedAt',
        render: (task: Task) => {
            return task.completedAt
                ? moment(task.completedAt).format('DD/MM/YYYY')
                : 'Not completed'
        },
    },
    DateToBeCompletedBy: {
        title: 'Date to be completed',
        key: 'dateToBeCompletedBy',
        render: (task: Task) => {
            return moment(task.dateToBeCompletedBy).format('DD/MM/YYYY')
        },
    },
    AssignedTo: {
        title: 'Assigned User',
        key: 'assignedUser',
        render: (task: Task) => {
            return task?.assignedUser
                ? `${task?.assignedUser?.firstName} ${task?.assignedUser?.lastName}`
                : 'None'
        },
    },
    Minutes: {
        title: 'Minutes',
        key: 'minutes',
        render: (task: Task) => {
            return task.minutes
        },
    },
} as const

const DEFAULT_COLUMNS = [
    TasksTableColumns.Type,
    TasksTableColumns.AssignedTo,
    TasksTableColumns.Author,
    TasksTableColumns.Minutes,
    TasksTableColumns.Description,
]

export default function TasksTable({
    request,
    columns,
    pagination,
    actionComponents,
    dependencies,
}: Props) {
    const [tasks, setTasks] = useState<Task[]>([])
    const [tasksAmount, setTasksAmount] = useState<number>(0)
    const rowsPerPageOptions = [10, 20, 50, 100]
    type RowsPerPageOptions = typeof rowsPerPageOptions[number] // 10 | 20 | 50 | 100
    const [page, setPage] = useState<number>(1)
    const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageOptions>(10)

    const getTasksDeps = useDebounce(JSON.stringify(dependencies), 200)

    const pageChange = (page: number, pageSize: number) => {
        setPage(page)
        setRowsPerPage(pageSize)
    }

    const tableColumns = useMemo(() => {
        let cols = columns ? Object.values(columns) : DEFAULT_COLUMNS
        if (actionComponents) {
            cols = [
                ...cols,
                {
                    title: 'Actions',
                    key: 'actions',
                    render: (task: Task) => {
                        return (<>{actionComponents.map((a) => a(task.id))}</>) as any
                    },
                },
            ]
        }
        return cols
    }, [columns])

    const getTasks = async (resetPage = false) => {
        const response = await request({
            skip: page * rowsPerPage - rowsPerPage,
            limit: rowsPerPage,
        })

        if (response.successful) {
            const { count, entities } = response.data
            resetPage && setPage(1)
            setTasksAmount(count)
            setTasks(entities)
        } else {
            toastFailure(response.message)
        }
    }

    // this happens when dependencies change outside the table, e.g filters given in with getTasksDeps
    useEffect(() => {
        getTasks(true)
    }, [getTasksDeps, rowsPerPage])

    // this happens when dependencies change outside the table, e.g filters given in with getTasksDeps
    useEffect(() => {
        getTasks()
    }, [page])

    return (
        <Table
            rowKey="id"
            columns={tableColumns}
            dataSource={tasks}
            expandable={undefined}
            size="small"
            pagination={
                pagination
                    ? {
                          defaultPageSize: 10,
                          current: page,
                          onChange: pageChange,
                          total: tasksAmount,
                      }
                    : false
            }
        />
    )
}
